import {UtilityColors} from "../components/charts/constants/chart-color-constants";
import * as color from "color";
import {from} from "linq";


export const defaultUtilities = ["Gas", "Water", "Electricity"];

/**
 * Default numeric mapper, converts data to a float with 2 fixed points
 * @param x - The number to process
 * @returns {number} The mapped number
 */
const defaultMapper = x => parseFloat(x.toFixed(2));

/**
 * Takes generic data with dates and rebuilds it for charts taking a given field as the value
 *
 * @param {array} data - A collection of date based information
 * @param {string} valueField - The field to treat as the value
 * @param {string} dateField - The field to treat as the date
 * @param {function(x: *) : *} valueProcessor - An optional processor for each value field matched, i.e convert to decimal/date or prefix string etc
 * @return {{date: string, value: * }[]} returns a conventional date based set of data for use with conventional charts
 *
 * @example
 *
 *     collateGenericData(someGenericData, "totalCons", "someDateField");
 */
export const collateGenericData = (data, valueField, dateField, valueProcessor = defaultMapper) => {
    return data.map(breakdown => {
            const result = { date: breakdown[dateField] };
            const fieldValue = breakdown[valueField];
            result.value = (!valueProcessor) ? fieldValue : valueProcessor(fieldValue+0);
            return result;
        });
};

/**
 * Takes cost and consumption data with breakdown from API and rebuilds it for charts taking a given field as the value
 *
 * @param {{date: string, breakdown: *}[]} data - A collection of date based utility breakdowns
 * @param {string} fieldToTake - The field to take from the breakdown (what field the chart should use)
 * @param {string[]} utilitiesToUse - The utilities to be processed, you dont always want Water in all and some charts may have more utility types
 * @param {function(x: *) : *} valueProcessor - An optional processor for each field matched, i.e convert to decimal/date or prefix string etc
 * @return {{date: string, gas: number, water: number, electricity: number }[]} returns a conventional utility dataset for chart usage
 *
 * @example
 *
 *     collateUtilityData(costAndConsumptionData, "totalCons");
 */
export const collateUtilityData = (data, fieldToTake, utilitiesToUse = defaultUtilities, valueProcessor = defaultMapper) => {
    const collatedData = data.map(x => {
        const indexesRemaining = [...utilitiesToUse];
        const data = {date: x.date};
        x.breakdown.forEach((breakdown) => {
            if(utilitiesToUse.indexOf(breakdown.utility) === -1) {return;}

            const utility = breakdown.utility.toLowerCase();
            const fieldValue = breakdown[fieldToTake];
            data[utility] = (!valueProcessor) ? fieldValue : valueProcessor(fieldValue+0);
            const indexToRemove = indexesRemaining.indexOf(breakdown.utility);
            indexesRemaining.splice(indexToRemove, 1);
        });

        indexesRemaining.forEach((index) => {
            data[index.toLowerCase()] = 0;
        });
        return data;
    });

    return from(collatedData).orderBy(x => x.date).toArray();
};

export const collateMeterCountData = (data, utility) => {
   var obj = {};

   data.forEach((x) => {
    if(x.utility === utility)
    {
        obj[x.meterType] = x.mS_Count
    }
   });

   var size = Object.keys(obj).length;
   if(size === 0)
    return [{"None": 0 }]
    else
   return [obj];
};

export const collateMeterCountDataAll = (data) => {
    var obj = {};
 
    data.forEach((x) => {
   
         obj[x.utility] = x.mS_Count
     
    });
 
    var size = Object.keys(obj).length;


    if(size === 0)
     return [{"None": 0 }]
     else
    return [obj];
 };


/**
 * Takes cost and consumption data with breakdown from API and sums a given field per utility
 *
 * @param {{date: string, breakdown: *}[]} data - A collection of date based utility breakdowns
 * @param {string} fieldToTake - The field to take from the breakdown (what field the chart should use)
 * @param {function(x: *) : *} valueProcessor - An optional processor for each summed field, i.e convert to decimal/date or prefix string etc
 * @return {{gas: number, water: number, electricity: number }} returns all utilities with the field summed
 *
 * @example
 *
 *     sumUtilityData(costAndConsumptionData, "totalCons");
 */
export const sumUtilityData = (data, fieldToTake, valueProcessor = defaultMapper) => {
    const types = ["gas", "electricity", "water"];
    const initialTotal = {gas: 0, electricity: 0, water: 0};

    const reducedData = collateUtilityData(data, fieldToTake, defaultUtilities, null).reduce((total, x) => {
        const reduced = {};
        for(const field of types) {
            reduced[field] = total[field] + x[field]; 
        }
        return reduced;
    }, initialTotal);

    if(valueProcessor) {
        for(const field of types) {
            reducedData[field] = valueProcessor(reducedData[field]+0); 
        }
    }

    return reducedData;
};

export const sumUtilityDataDonut = (data, fieldToTake, valueProcessor = defaultMapper) => {
    const types = ["gas", "electricity"];
    const initialTotal = {gas: 0, electricity: 0};

    const reducedData = collateUtilityData(data, fieldToTake, defaultUtilities, null).reduce((total, x) => {
        const reduced = {};
        for(const field of types)
        { reduced[field] = total[field] + x[field]; }
        return reduced;
    },initialTotal);

    if(valueProcessor)
    {
        for(const field of types)
        { reducedData[field] = valueProcessor(reducedData[field]+0); }
    }

    return reducedData;
};

export const createDonutDataSetFrom = (data, fieldToMap, colorToUse = UtilityColors.gas) => {
  
    const isFunctionMapper = typeof fieldToMap === "function";
  return {
        data: data.map(x => isFunctionMapper? fieldToMap(x) : x[fieldToMap]),
        backgroundColor : colorToUse
    }


};

export const createLineDataSetFrom = (data, fieldToMap, label, colorToUse = UtilityColors.gas, yAxisId = 0) => {
    const chartBaseColor = color(colorToUse);
    const chartBorderColor = chartBaseColor.string();
    const isFunctionMapper = typeof fieldToMap === "function";
    
    
    

    return {
        data: data.map(x => isFunctionMapper? fieldToMap(x) : x[fieldToMap]),
        backgroundColor: colorToUse, //bar colour
        borderCapStyle: "",
        borderColor: chartBorderColor,
        borderWidth: 1,
        fill: false,
        label: label,
        lineTension: 0.1,
        pointBackgroundColor: colorToUse,
        pointBorderColor: chartBorderColor,
        pointHoverBackgroundColor: colorToUse,
        pointHoverBorderColor: chartBorderColor,
        pointHoverBorderWidth: 1,
        pointHoverRadius: 4,
        pointRadius: 3,
        pointStyle: "circle",
        yAxisID: yAxisId
    }

    
};

export const createBarDataSetFrom = (data, fieldToMap, label, colorToUse = UtilityColors.gas, yAxisId = 0) => {
    const chartBaseColor = color(colorToUse);
    const chartBorderColor = chartBaseColor.lighten(0.3).string();
    const isFunctionMapper = typeof fieldToMap === "function";
    
    return {
        data: data.map(x => isFunctionMapper? fieldToMap(x) : x[fieldToMap]),
        backgroundColor: colorToUse, //bar colour
        borderCapStyle: "",
        fill: true,
        label: label,
        lineTension: 0.1,
        hoverBorderColor: chartBorderColor,
        hoverBorderWidth: 2,
        pointStyle: "circle",
        yAxisID: yAxisId
    }
};